import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { todayLocalIso } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import DetailDrawer from "@/components/DetailDrawer";
import SwingProgressTracker from "@/components/SwingProgressTracker";
import CustomJobsCard from "@/components/CustomJobsCard";
import {
  Wrench,
  ClipboardList,
  PenTool,
  Loader2,
  ChevronRight,
  Zap,
  Radio,
  ShieldAlert,
  CheckCircle2,
  Signature,
  PlayCircle,
  Check,
  AlertTriangle,
  Search,
  X as XIcon,
} from "lucide-react";

/**
 * SUPERVISOR view — action-first. Pending approvals at the top.
 * One tap opens item with signature pad already expanded → one tap to sign.
 */
export default function SupervisorDashboard() {
  const { user } = useAuth();
  const [queue, setQueue] = useState(null);
  const [todayHandovers, setTodayHandovers] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [selected, setSelected] = useState(null);

  // Swing-start state
  const [assets, setAssets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [swingSel, setSwingSel] = useState(new Set());
  const [assetOverride, setAssetOverride] = useState({});     // asset_id -> template_id
  const [assetQuery, setAssetQuery] = useState("");
  const [swingLabel, setSwingLabel] = useState("");
  const [swingBusy, setSwingBusy] = useState(false);
  const [swingMsg, setSwingMsg] = useState(null);
  const [swingTrackerKey, setSwingTrackerKey] = useState(0);

  const reload = async () => {
    const pendingStatuses = ["pending", "pending_signoff", "awaiting", "submitted", "in_review", "open", "waiting"];
    const isPending = (it) => {
      const s = String(it.status || it.stage || "").toLowerCase();
      if (!s) return false;
      return pendingStatuses.some((k) => s.includes(k));
    };

    const results = await Promise.allSettled([
      api.get("/breakdowns"),
      api.get("/handovers"),
      api.get("/ppfs"),
      api.get("/inspections"),
      api.get("/hazards/pending"),
      api.get("/assets"),
      api.get("/inspection-templates"),
    ]);
    const list = (r) => {
      if (r.status !== "fulfilled") return [];
      const d = r.value.data;
      return Array.isArray(d) ? d : d?.items || d?.results || d?.data || [];
    };
    const breakdowns = list(results[0]);
    const handovers = list(results[1]);
    const ppfs = list(results[2]);
    const inspections = list(results[3]);
    const hazards = list(results[4]);
    const assetsList = list(results[5]);
    const templatesList = list(results[6]);
    setAssets(assetsList);
    setTemplates(templatesList);

    const q = [
      ...breakdowns.filter(isPending).map((it) => ({
        ...it,
        _kind: "breakdown",
        _endpoint: (id) => `/breakdowns/${id}/approve`,
        _verb: "approve",
        _requireSig: false,
        _payload: () => ({}),
      })),
      ...ppfs.filter(isPending).map((it) => ({
        ...it,
        _kind: "ppf",
        _endpoint: (id) => `/ppfs/${id}/complete`,
        _verb: "complete",
        _requireSig: true,
        _payload: ({ signature, notes, attach }) => ({
          completed_by_sig: signature,
          completion_notes: notes,
          parts_used: (attach || []).map((a) => a.name || "photo"),
        }),
      })),
      ...inspections.filter(isPending).map((it) => ({
        ...it,
        _kind: "inspection",
        _endpoint: (id) => `/inspections/${id}/approve`,
        _verb: "approve",
        _requireSig: true,
        _payload: ({ signature, notes, item }) => ({
          supervisor_sig: signature,
          supervisor_name: item?.supervisor_name || user?.name || "",
          notes,
          hours: null,
        }),
      })),
    ].sort((a, b) => (new Date(b.created_at || 0) - new Date(a.created_at || 0)));
    setQueue(q);

    // today's activity
    const today = new Date().toDateString();
    setTodayHandovers(handovers.filter((h) => {
      const d = h.created_at || h.date || h.ts;
      return d && new Date(d).toDateString() === today;
    }));

    // flagged (critical / high / pending hazards)
    setFlagged(
      [...breakdowns, ...hazards].filter((it) => {
        const p = String(it.priority || it.severity || "").toLowerCase();
        return p === "critical" || p === "high";
      }).slice(0, 6)
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload().catch(() => {}); }, []);

  const openSignOff = (it) => setSelected(it);

  // ------- Start Swing (asset-first, auto template match) -------
  const swingAssets = useMemo(() => (
    (assets || []).filter((a) => {
      if (a.retired) return false;
      const s = String(a.status || "").toLowerCase();
      return !["retired", "decommissioned", "sold"].includes(s);
    })
  ), [assets]);

  // Map each template to its normalised machine_type for auto-matching.
  const templatesByMachineType = useMemo(() => {
    const m = new Map();
    (templates || []).forEach((t) => {
      const mt = norm(t.machine_type || t.applies_to || "");
      if (!mt) return;
      if (!m.has(mt)) m.set(mt, []);
      m.get(mt).push(t);
    });
    return m;
  }, [templates]);

  const templateForAsset = (asset) => {
    if (!asset) return null;
    const at = norm(asset.asset_type || asset.category || asset.machine_model || "");
    if (!at) return null;
    // exact match
    if (templatesByMachineType.has(at)) return templatesByMachineType.get(at)[0];
    // fuzzy: any template.machine_type contained in asset_type or vice-versa
    for (const [mt, list] of templatesByMachineType) {
      if (at.includes(mt) || mt.includes(at)) return list[0];
    }
    return null;
  };

  const addAsset = (assetId) => {
    setSwingSel((prev) => new Set(prev).add(String(assetId)));
    setAssetQuery("");
    setAssetOverride((prev) => {
      const next = { ...prev };
      const asset = swingAssets.find((a) => String(a.id || a._id) === String(assetId));
      const auto = templateForAsset(asset);
      if (auto) next[String(assetId)] = auto.id || auto._id;
      return next;
    });
  };
  const removeAsset = (assetId) => {
    setSwingSel((prev) => { const n = new Set(prev); n.delete(String(assetId)); return n; });
    setAssetOverride((prev) => { const n = { ...prev }; delete n[String(assetId)]; return n; });
  };
  const clearSwing = () => { setSwingSel(new Set()); setAssetOverride({}); };
  const setOverride = (assetId, templateId) => {
    setAssetOverride((prev) => ({ ...prev, [String(assetId)]: templateId }));
  };

  const selectedAssetList = useMemo(() => (
    Array.from(swingSel).map((id) => swingAssets.find((a) => String(a.id || a._id) === String(id))).filter(Boolean)
  ), [swingSel, swingAssets]);

  const swingReady = selectedAssetList.length > 0 &&
    selectedAssetList.every((a) => assetOverride[String(a.id || a._id)]);

  const assetSuggestions = useMemo(() => {
    const q = assetQuery.trim().toLowerCase();
    const base = swingAssets.filter((a) => !swingSel.has(String(a.id || a._id)));
    if (!q) return base.slice(0, 12);
    return base.filter((a) => {
      const hay = [a.id, a.name, a.machine_model, a.asset_type, a.category, a.bucket_number]
        .filter(Boolean).map((x) => String(x).toLowerCase()).join(" ");
      return hay.includes(q);
    }).slice(0, 12);
  }, [assetQuery, swingAssets, swingSel]);

  const startSwing = async () => {
    if (!swingReady || swingBusy) return;
    setSwingBusy(true); setSwingMsg(null);
    const now = new Date();
    const today = todayLocalIso(now);
    const endDate = todayLocalIso(new Date(now.getTime() + 12 * 3600 * 1000));
    try {
      const items = selectedAssetList.map((a) => ({
        asset_id: String(a.id || a._id),
        template_id: assetOverride[String(a.id || a._id)],
      }));
      const { data } = await api.post("/swings", {
        items,
        swing_label: swingLabel || `Start of Swing · ${today}`,
        start_date: today,
        end_date: endDate,
        work_area: user?.site_code || "",
        notes: `Started by ${user?.name || user?.username || "supervisor"} at ${now.toLocaleString()}`,
        client_op_id: `swing-${now.getTime()}`,
      });
      setSwingBusy(false);
      setSwingMsg({ type: "ok", text: `Swing started · ${items.length} asset${items.length === 1 ? "" : "s"} · ${data?.id ? "#" + String(data.id).slice(0, 8) : "created"}.` });
      clearSwing(); reload().catch(() => {});
      setSwingTrackerKey((k) => k + 1);
    } catch (ex) {
      if (ex?.response?.status === 404) {
        const res = await Promise.allSettled(
          selectedAssetList.map((a) => api.post("/inspections", {
            asset_id: String(a.id || a._id),
            template_id: assetOverride[String(a.id || a._id)],
            shift: currentShift(),
            client_op_id: `insp-${a.id || a._id}-${now.getTime()}`,
          }))
        );
        const ok = res.filter((r) => r.status === "fulfilled").length;
        const fail = res.length - ok;
        setSwingBusy(false);
        setSwingMsg(
          fail === 0
            ? { type: "ok", text: `Swing started · ${ok} inspection${ok === 1 ? "" : "s"} created.` }
            : { type: "err", text: `${ok} started, ${fail} failed. ${apiError(res.find((r) => r.status === "rejected")?.reason)}` }
        );
        if (ok > 0) { clearSwing(); reload().catch(() => {}); setSwingTrackerKey((k) => k + 1); }
      } else {
        setSwingBusy(false);
        setSwingMsg({ type: "err", text: apiError(ex) });
      }
    }
  };

  return (
    <div className="space-y-8" data-testid="supervisor-dashboard">
      {/* Hero */}
      <div className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-black text-muted">
            <Radio className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
            Supervisor Console · {user?.site_code || "All sites"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mt-2">
            What needs<br className="hidden md:block" />
            <span style={{ color: "var(--primary)" }}>your signature.</span>
          </h1>
          <p className="text-muted mt-3 max-w-xl">
            Tap any item to open · draw signature · one tap to submit.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-muted">Waiting on you</div>
          <div className="font-display font-black text-6xl md:text-7xl tracking-tight leading-none" style={{ color: "var(--primary)" }}>
            {queue === null ? <Loader2 className="w-8 h-8 animate-spin inline" /> : queue.length}
          </div>
        </div>
      </div>

      {/* Start Swing — asset-first search, auto-match template */}
      <section className="panel-heavy p-5 md:p-6" data-testid="swing-starter">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <PlayCircle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Start of swing</div>
            <div className="font-display font-black text-xl tracking-tight">Add an Asset</div>
            <div className="text-xs text-muted mt-0.5">Type the asset number — we&apos;ll pick the right inspection for a drill, truck or digger.</div>
          </div>
        </div>

        {/* Asset search input */}
        <div className="relative mb-3">
          <div className="panel-flat px-3 flex items-center gap-2">
            <Search className="w-4 h-4 text-muted shrink-0" />
            <input
              className="w-full py-3 bg-transparent outline-none text-base font-mono-tight"
              value={assetQuery}
              onChange={(e) => setAssetQuery(e.target.value)}
              placeholder="Asset number — e.g. DR-12, TR-45, DIG-08"
              data-testid="swing-asset-search"
              autoComplete="off"
            />
            {assetQuery && (
              <button className="btn btn-sm btn-ghost !py-1 !px-2" onClick={() => setAssetQuery("")} title="Clear">
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {(assetQuery || (swingAssets.length > 0 && swingSel.size === 0)) && assetSuggestions.length > 0 && (
            <div className="absolute left-0 right-0 mt-1 rounded-lg z-20 max-h-72 overflow-auto"
                 style={{ background: "var(--surface)", border: "1px solid var(--border)", boxShadow: "0 12px 32px rgba(0,0,0,0.5)" }}
                 data-testid="swing-asset-suggestions">
              {assetSuggestions.map((a) => {
                const id = a.id || a._id;
                const auto = templateForAsset(a);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => addAsset(id)}
                    className="w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-white/5 border-b last:border-b-0"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`swing-suggestion-${id}`}
                  >
                    <div className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center font-display font-black text-xs uppercase"
                         style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                      {shortType(a.asset_type || a.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black text-sm truncate flex items-center gap-2">
                        <span className="font-mono-tight">{a.id || id}</span>
                        <span className="text-muted">·</span>
                        <span className="truncate">{a.name || a.machine_model}</span>
                      </div>
                      <div className="text-[11px] text-muted flex flex-wrap items-center gap-x-2">
                        <span>{(a.asset_type || a.category || "Asset")}</span>
                        {a.machine_model && <span>· {a.machine_model}</span>}
                        {auto ? (
                          <span className="pill pill-green !text-[10px] !py-0.5 !px-2"><Check className="w-3 h-3" />{auto.name || auto.title}</span>
                        ) : (
                          <span className="pill pill-orange-solid !text-[10px] !py-0.5 !px-2"><AlertTriangle className="w-3 h-3" />No auto-template</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Swing label */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="field">
            <label>Swing Label</label>
            <input
              className="input"
              value={swingLabel}
              onChange={(e) => setSwingLabel(e.target.value)}
              placeholder={`e.g. Day Shift · ${new Date().toLocaleDateString()}`}
              data-testid="swing-label"
            />
          </div>
          <div className="field">
            <label>Work Area</label>
            <input
              className="input"
              value={user?.site_code || ""}
              readOnly
              data-testid="swing-work-area"
            />
          </div>
        </div>

        {/* Selected asset chips */}
        {selectedAssetList.length > 0 && (
          <div className="grid gap-2 mb-4" data-testid="swing-selected-list">
            {selectedAssetList.map((a) => {
              const id = String(a.id || a._id);
              const tid = assetOverride[id];
              const chosen = templates.find((t) => String(t.id || t._id) === String(tid));
              const auto = templateForAsset(a);
              return (
                <div key={id} className="panel p-3.5 flex items-center gap-3" data-testid={`swing-chip-${id}`}>
                  <div className="w-10 h-10 rounded-lg shrink-0 flex items-center justify-center font-display font-black text-xs"
                       style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                    {shortType(a.asset_type || a.category)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-sm truncate flex items-center gap-2">
                      <span className="font-mono-tight">{a.id || id}</span>
                      <span className="text-muted">·</span>
                      <span className="truncate">{a.name || a.machine_model || "Asset"}</span>
                    </div>
                    <div className="text-[11px] text-muted mt-0.5 flex flex-wrap items-center gap-2">
                      {chosen ? (
                        <span className="pill pill-green !text-[10px] !py-0.5 !px-2">
                          <Check className="w-3 h-3" />{chosen.name || chosen.title || "Template"}
                          {auto && String(auto.id || auto._id) === String(chosen.id || chosen._id) && <span className="opacity-80">· auto</span>}
                        </span>
                      ) : (
                        <span className="pill pill-red !text-[10px] !py-0.5 !px-2"><AlertTriangle className="w-3 h-3" />Pick template</span>
                      )}
                      <select
                        className="input !py-1 !px-2 !text-[11px] !max-w-[220px]"
                        value={tid || ""}
                        onChange={(e) => setOverride(id, e.target.value)}
                        data-testid={`swing-template-${id}`}
                      >
                        <option value="">— Change template —</option>
                        {templates.map((t) => (
                          <option key={t.id || t._id} value={t.id || t._id}>
                            {(t.name || t.title || t.template_name || "Template")}{t.machine_type ? ` · ${t.machine_type}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button className="btn btn-sm btn-ghost" onClick={() => removeAsset(id)} data-testid={`swing-remove-${id}`}>
                    <XIcon className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-4"
             style={{ borderTop: "1px solid var(--border)" }}>
          <div className="text-sm">
            <span className="font-display font-black text-2xl tracking-tight" style={{ color: "var(--primary)" }}>{selectedAssetList.length}</span>
            <span className="text-muted ml-2">asset{selectedAssetList.length === 1 ? "" : "s"} on this swing</span>
          </div>
          <div className="flex gap-2">
            {selectedAssetList.length > 0 && (
              <button className="btn btn-ghost" onClick={clearSwing}>Clear</button>
            )}
            <button
              className="btn btn-primary"
              style={{ minHeight: 56, fontSize: 16, padding: "0 22px" }}
              onClick={startSwing}
              disabled={!swingReady || swingBusy}
              data-testid="swing-start-btn"
            >
              {swingBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : <PlayCircle className="w-5 h-5" />}
              Start Swing
            </button>
          </div>
        </div>

        {swingMsg && (
          <div className={`mt-3 pill ${swingMsg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}
               data-testid="swing-status">
            {swingMsg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            {swingMsg.text}
          </div>
        )}
      </section>

      {/* Swing Progress Tracker — live, per-inspection progress bars */}
      <SwingProgressTracker key={swingTrackerKey} />

      {/* Custom jobs — bucket sheets, GET wear, site-specific forms */}
      <CustomJobsCard />

      {/* Approval queue */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">
            Pending approvals
          </div>
          <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          <PenTool className="w-4 h-4 text-primary-brand" />
        </div>

        {queue === null ? (
          <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading your queue…
          </div>
        ) : queue.length === 0 ? (
          <div className="panel-heavy p-10 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2" style={{ color: "var(--success, #22c55e)" }} />
            <div className="font-display font-black text-2xl tracking-tight">All clear.</div>
            <div className="text-muted text-sm mt-1">Nothing waiting on your signature.</div>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="approval-queue">
            {queue.map((it, i) => (
              <button
                key={`${it._kind}-${it.id || i}`}
                className="panel p-5 md:p-6 text-left flex items-center gap-4"
                onClick={() => openSignOff(it)}
                data-testid={`queue-item-${i}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                  {iconOf(it._kind)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="pill pill-orange">{kindLabel(it._kind)}</span>
                    {it.priority && <span className={`pill ${priorityClass(it.priority)}`}>{it.priority}</span>}
                    {it.status && <span className="pill">{it.status}</span>}
                  </div>
                  <div className="font-display font-black text-lg md:text-xl truncate">
                    {labelOf(it)}
                  </div>
                  <div className="text-xs text-muted mt-1 truncate">
                    {(it.site_code || it.site) && <>Site: <b>{it.site_code || it.site}</b> · </>}
                    {(it.asset_id || it.asset_name) && <>Asset: <b>{it.asset_id || it.asset_name}</b> · </>}
                    {(it.created_by_name || it.reported_by) && <>By: <b>{it.created_by_name || it.reported_by}</b></>}
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 shrink-0 pl-3" style={{ borderLeft: "1px dashed var(--border)" }}>
                  <Signature className="w-4 h-4 text-primary-brand" />
                  <span className="font-display font-black text-sm uppercase tracking-widest text-primary-brand">
                    Sign
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted shrink-0" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Two-column: today + flagged */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="panel-heavy p-5">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-primary-brand" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Today · Shift handovers</div>
          </div>
          {todayHandovers.length === 0 ? (
            <div className="text-muted text-sm py-3">None logged today.</div>
          ) : (
            <div className="divide-border">
              {todayHandovers.slice(0, 6).map((h, i) => (
                <div key={i} className="py-3 first:pt-0">
                  <div className="font-display font-black text-sm truncate">
                    {h.summary || h.site_code || "Handover"}
                  </div>
                  <div className="text-xs text-muted">
                    {h.shift || ""} · {h.created_by_name || ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="panel-heavy p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-primary-brand" />
            <div className="font-display font-black uppercase tracking-widest text-sm">Flagged · High &amp; Critical</div>
          </div>
          {flagged.length === 0 ? (
            <div className="text-muted text-sm py-3">Nothing flagged.</div>
          ) : (
            <div className="divide-border">
              {flagged.map((f, i) => (
                <button key={i} className="w-full text-left py-3 first:pt-0" onClick={() => openSignOff({ ...f, _kind: f._kind || (f.severity ? "hazard" : "breakdown") })}>
                  <div className="flex items-center gap-2">
                    <span className={`pill ${priorityClass(f.priority || f.severity)}`}>{f.priority || f.severity}</span>
                    <span className="font-display font-black text-sm truncate">{labelOf(f)}</span>
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {f.site_code || f.site} · {f.asset_name || ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
        <Link to="/handovers" className="btn btn-ghost btn-sm">All handovers</Link>
        <Link to="/breakdowns" className="btn btn-ghost btn-sm">All breakdowns</Link>
        <Link to="/ppfs" className="btn btn-ghost btn-sm">All Planning Requests</Link>
        <Link to="/inspections" className="btn btn-ghost btn-sm">All inspections</Link>
      </div>

      {selected && (
        <DetailDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { setSelected(null); reload(); }}
          itemLabel={labelOf}
          resourceName={selected._kind || "Record"}
          openSignOffOnMount={true}
          signOffConfig={
            selected._endpoint
              ? {
                  endpoint: (it) => selected._endpoint(it.id || it._id),
                  label: `Sign & ${selected._verb}`,
                  verb: selected._verb,
                  requireSignature: selected._requireSig !== false,
                  payload: selected._payload,
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function iconOf(kind) {
  if (kind === "breakdown") return <Wrench className="w-5 h-5" />;
  if (kind === "handover") return <ClipboardList className="w-5 h-5" />;
  if (kind === "ppf") return <PenTool className="w-5 h-5" />;
  if (kind === "inspection") return <ClipboardList className="w-5 h-5" />;
  if (kind === "hazard") return <ShieldAlert className="w-5 h-5" />;
  return <PenTool className="w-5 h-5" />;
}
function labelOf(it) {
  return it.title || it.summary || it.description || it.template_name || it.asset_name || it.name || "Record";
}

// Human-facing label for the queue-item kind pill. Internal kind stays as
// "ppf" so downstream code (icons, routing, filtering) is unchanged.
function kindLabel(kind) {
  switch (kind) {
    case "ppf": return "Planning Request";
    case "breakdown": return "Breakdown";
    case "handover": return "Handover";
    case "inspection": return "Inspection";
    case "hazard": return "Hazard";
    default: return kind || "Record";
  }
}
function priorityClass(p) {
  const s = String(p || "").toLowerCase();
  if (s === "critical") return "pill-red-solid";
  if (s === "high") return "pill-orange-solid";
  if (s === "medium") return "pill-blue";
  return "pill";
}

function currentShift() {
  const h = new Date().getHours();
  if (h >= 6 && h < 14) return "morning";
  if (h >= 14 && h < 22) return "afternoon";
  return "night";
}

function norm(v) {
  return String(v || "").toLowerCase().replace(/[_\-\s]+/g, "");
}
function shortType(t) {
  const s = String(t || "").toLowerCase();
  if (s.includes("truck")) return "TRK";
  if (s.includes("drill")) return "DRL";
  if (s.includes("dig") || s.includes("exc")) return "DIG";
  if (s.includes("dozer")) return "DOZ";
  if (s.includes("load")) return "LDR";
  if (s.includes("grade")) return "GRD";
  return (s.slice(0, 3) || "AST").toUpperCase();
}
